import type { OtpProductCheckoutRow } from "./useOtpProducts";

/** Resolves the canonical Studio workspace OTP row for checkout (mirrors backend resolution). */
export function resolveDefaultWorkspaceOtpProduct(
  products: OtpProductCheckoutRow[]
): OtpProductCheckoutRow | null {
  const flaggedStudio = products.find((p) => p.is_default_workspace_otp && p.tier === "studio");
  if (flaggedStudio) {
    return flaggedStudio;
  }
  const namedStudio = products.find((p) => p.tier === "studio" && p.name === "Studio");
  if (namedStudio) {
    return namedStudio;
  }
  const studios = products.filter((p) => p.tier === "studio");
  if (studios.length === 0) {
    return null;
  }
  return [...studios].sort((a, b) => a.price - b.price)[0] ?? null;
}

function formatUsdTwoDecimals(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/** MSRP delta Studio − Writer from checkout catalog (matches backend upgrade checkout). */
export function getWriterToStudioUpgradeDisplayUsd(
  products: OtpProductCheckoutRow[],
  fallback = "$100.01"
): string {
  const studio = resolveDefaultWorkspaceOtpProduct(products);
  const writer = products.find((p) => p.tier === "writer");
  if (!studio || !writer) {
    return fallback;
  }
  const cents = Math.round(Number(studio.price) * 100) - Math.round(Number(writer.price) * 100);
  if (!Number.isFinite(cents) || cents <= 0) {
    return fallback;
  }
  return formatUsdTwoDecimals(cents / 100);
}

export function getWriterToStudioUpgradeCreditDelta(
  products: OtpProductCheckoutRow[],
  fallback = 70
): number {
  const studio = resolveDefaultWorkspaceOtpProduct(products);
  const writer = products.find((p) => p.tier === "writer");
  if (!studio || !writer) {
    return fallback;
  }
  const delta =
    Math.floor(Number(studio.included_ai_credits)) - Math.floor(Number(writer.included_ai_credits));
  if (!Number.isFinite(delta) || delta <= 0) {
    return fallback;
  }
  return delta;
}
